// const API_BASE_URL = 'http://127.0.1:5000'; // Update this to your actual backend URL
const API_BASE_URL='https://seminar-hall-backend-two.vercel.app'


class BookingAPI {
  constructor() {
    this.bookings = [];
    this.cachedToken = null; // Cache token for performance
  }

  // Token management methods
  getAuthToken() {
    try {
      // Check cache first
      if (this.cachedToken) {
        return this.cachedToken;
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        const token = parsedData.token || parsedData.accessToken || null;
        this.cachedToken = token; // Cache the token
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      this.cachedToken = null;
      return null;
    }
  }

  // Clear cached token (called during logout)
  clearTokenCache() {
    this.cachedToken = null;
    // console.log('🧹 Token cache cleared');
  }

  // Debug method to check storage state
  debugStorageState() {
    // console.log('🔍 Storage Debug:');
    // console.log('localStorage.userData:', localStorage.getItem('userData'));
    // console.log('localStorage.authToken:', localStorage.getItem('authToken'));
    // console.log('sessionStorage.userData:', sessionStorage.getItem('userData'));
    // console.log('cachedToken:', this.cachedToken);
    // console.log('getAuthToken():', this.getAuthToken());
  }

  getAuthHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  async testConnection() {
    try {
      // console.log('🔗 Testing API connection to:', API_BASE_URL);
      const response = await fetch(`${API_BASE_URL}/api/halls`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      // console.log('🔍 Connection test response:', response.status);
      
      if (response.ok) {
        // console.log('✅ API connection successful, found', data.data?.length || 0, 'halls');
        return { success: true, connected: true };
      } else {
        // console.log('❌ API connection failed:', response.status, response.statusText);
        return { success: false, connected: false, error: `HTTP ${response.status}` };
      }
    } catch (error) {
      console.error('💥 API connection error:', error);
      return { success: false, connected: false, error: error.message };
    }
  }

  async getHalls() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/halls`, {
        headers: this.getAuthHeaders(),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching halls:', error);
      return {
        success: false,
        error: 'Failed to load halls. Please check your connection.',
        message: 'Unable to connect to the server'
      };
    }
  }

  async getTimeSlots(hallId, selectedDate = null, userContext = null) {
    try {
      const date = selectedDate || new Date().toISOString().split('T')[0];
      let url = `${API_BASE_URL}/api/halls/${hallId}/timeslots?date=${date}`;
      
      if (userContext && userContext.userId) {
        url += `&userId=${userContext.userId}`;
      }
      if (userContext && userContext.userEmail) {
        url += `&userEmail=${encodeURIComponent(userContext.userEmail)}`;
      }
      if (userContext && userContext.userPhone) {
        url += `&userPhone=${encodeURIComponent(userContext.userPhone)}`;
      }
      
      // console.log('🚀 Making API request to:', url);
      // console.log('📝 Request params:', { hallId, date, userContext });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: this.getAuthHeaders()
        });
        clearTimeout(timeoutId);
        // console.log('📊 Response status:', response.status);
      
        if (!response.ok) {
          console.error('❌ Network response was not ok:', response.status, response.statusText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        // console.log('📦 Response data:', data);
        
        return data;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('⏰ Request timed out after 10 seconds');
          throw new Error('Request timed out. Please check your connection and try again.');
        }
        console.error('🚨 Fetch error:', fetchError);
        throw fetchError;
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      return {
        success: false,
        error: 'Failed to load time slots. Please try again.',
        message: 'Unable to connect to the server'
      };
    }
  }

  async submitBooking(bookingData) {
    try {
      // console.log('API: Sending POST request to:', `${API_BASE_URL}/api/bookings`);
      // console.log('API: Request body:', JSON.stringify(bookingData, null, 2));
      
      const response = await fetch(`${API_BASE_URL}/api/bookings`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(bookingData),
      });
      
      // console.log('API: Response status:', response.status);
      
      const responseText = await response.text();
      // console.log('API: Raw response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('API: Failed to parse JSON response:', parseError);
        return {
          success: false,
          error: `Server returned invalid JSON (Status: ${response.status})`,
          message: responseText.substring(0, 200),
          status: response.status
        };
      }
      
      if (!response.ok) {
        console.error('API: HTTP Error:', response.status, data);
        return {
          success: false,
          error: data.error || data.message || `HTTP ${response.status}`,
          message: data.details || 'Server error occurred',
          status: response.status,
          data: data
        };
      }
      
      // console.log('API: Success response:', data);
      return data;
    } catch (error) {
      console.error('API: Network/Connection error:', error);
      return {
        success: false,
        error: 'Failed to submit booking. Please try again.',
        message: 'Unable to connect to the server',
        networkError: error.message
      };
    }
  }

  async getAllBookings() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings`, {
        headers: this.getAuthHeaders(),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching bookings:', error);
      return {
        success: false,
        error: 'Failed to load bookings',
        message: 'Unable to connect to the server'
      };
    }
  }

  async approveBooking(bookingId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/approve`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error approving booking:', error);
      return {
        success: false,
        error: 'Failed to approve booking',
        message: 'Unable to connect to the server'
      };
    }
  }

  async rejectBooking(bookingId, reason = 'No reason provided') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/reject`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ reason }),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error rejecting booking:', error);
      return {
        success: false,
        error: 'Failed to reject booking',
        message: 'Unable to connect to the server'
      };
    }
  }

  async updateBookingStatus(bookingId, status, reason = null) {
    try {
      const endpoint = status === 'approved' ? 'approve' : 'reject';
      const body = status === 'rejected' && reason ? JSON.stringify({ reason }) : null;
      
      const response = await fetch(`${API_BASE_URL}/api/bookings/${bookingId}/${endpoint}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: body,
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error ${status} booking:`, error);
      return {
        success: false,
        error: `Failed to ${status} booking`,
        message: 'Unable to connect to the server'
      };
    }
  }

  async getDashboardStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // If stats endpoint doesn't exist, return empty stats
        return {
          success: true,
          data: {
            totalBookings: 0,
            pendingBookings: 0,
            approvedBookings: 0,
            rejectedBookings: 0
          }
        };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        success: true,
        data: {
          totalBookings: 0,
          pendingBookings: 0,
          approvedBookings: 0,
          rejectedBookings: 0
        }
      };
    }
  }

  // Add report generation methods
  async generateBookingsReport(startDate, endDate, format) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/bookings?startDate=${startDate}&endDate=${endDate}&format=${format}`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Report generation failed');
      }
      const blob = await response.blob();
      return { success: true, blob };
    } catch (error) {
      console.error('Error generating bookings report:', error);
      return { success: false, error: 'Failed to generate report' };
    }
  }

  async generateHallUtilizationReport(startDate, endDate, format) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/halls?startDate=${startDate}&endDate=${endDate}&format=${format}`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Report generation failed');
      }
      const blob = await response.blob();
      return { success: true, blob };
    } catch (error) {
      console.error('Error generating hall utilization report:', error);
      return { success: false, error: 'Failed to generate report' };
    }
  }

  async generateAnalyticsReport(startDate, endDate, format) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reports/analytics?startDate=${startDate}&endDate=${endDate}&format=${format}`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Report generation failed');
      }
      const blob = await response.blob();
      return { success: true, blob };
    } catch (error) {
      console.error('Error generating analytics report:', error);
      return { success: false, error: 'Failed to generate report' };
    }
  }

  // Unified Authentication APIs
  async userLogin(email, password) {
    try {
      // console.log('API: Unified login attempt for:', email);
      
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      // console.log('API: Unified login response:', data);

      if (response.ok && data.success) {
        // console.log('API: Login successful:', data.data);
        return data;
      } else {
        return {
          success: false,
          error: data.error || 'Login failed',
          message: data.message || 'Invalid credentials'
        };
      }
    } catch (error) {
      console.error('API: Login error:', error);
      return {
        success: false,
        error: 'Login failed',
        message: 'Unable to connect to the server',
        networkError: error.message
      };
    }
  }

  async userSignup(userData) {
    try {
      // console.log('API: User signup attempt for:', userData.email);
      
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      // console.log('API: User signup response:', data);

      if (response.ok && data.success) {
        // console.log('API: User signup successful:', data.data);
        return data;
      } else {
        return {
          success: false,
          error: data.error || 'Signup failed',
          message: data.message || 'Failed to create account'
        };
      }
    } catch (error) {
      console.error('API: User signup error:', error);
      return {
        success: false,
        error: 'Signup failed',
        message: 'Unable to connect to the server',
        networkError: error.message
      };
    }
  }

  async userLogout() {
    try {
      // console.log('🚪 API: Starting logout process...');

      // Debug storage state before logout
      // console.log('📊 Before logout:');
      this.debugStorageState();

      // Get current token before clearing for server logout call


      // Clear cached token immediately
      this.clearTokenCache();

      // Clear local storage immediately
      localStorage.removeItem('userData');
      localStorage.removeItem('authToken');  // Clear any standalone token storage
      localStorage.removeItem('user');       // Clear any other user data

      // Clear session storage as well (in case it's used)
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');

      // console.log('🧹 All local storage and token cache cleared during logout');

      // Debug storage state after clearing
      // console.log('📊 After clearing:');
      this.debugStorageState();



      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      console.error('API: User logout error:', error);

      // Ensure local storage is cleared even if there's an error
      localStorage.removeItem('userData');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');

      return {
        success: true,
        message: 'Logged out successfully'
      };
    }
  }

  async getCurrentUser() {
    try {
      // Check localStorage for user data
      const userData = localStorage.getItem('userData');
      if (!userData) {
        return {
          success: false,
          error: 'No user session found',
          message: 'Please log in'
        };
      }

      const parsedUserData = JSON.parse(userData);
      
      // Return local data directly for faster authentication
      return {
        success: true,
        data: parsedUserData
      };
      
    } catch (error) {
      console.error('API: Get current user error:', error);
      
      return {
        success: false,
        error: 'Failed to get user session',
        message: 'Please log in again'
      };
    }
  }

  async getMyBookings(userEmail) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings/my-bookings?userEmail=${encodeURIComponent(userEmail)}`, {
        headers: this.getAuthHeaders(),
      });
      const data = await response.json();
      return data;
    } catch (error) {
      return { success: false, error: 'Failed to load your bookings' };
    }
  }

  async adminLogin(email, password) {
    try {
      // console.log('API: Admin login attempt for:', email);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      // console.log('API: Admin login response:', data);

      if (response.ok && data.success) {
        // console.log('API: Admin login successful:', data.data);
        return data;
      } else {
        return {
          success: false,
          error: data.error || 'Login failed',
          message: data.message || 'Invalid credentials'
        };
      }
    } catch (error) {
      console.error('API: Admin login error:', error);
      return {
        success: false,
        error: 'Login failed',
        message: 'Unable to connect to the server',
        networkError: error.message
      };
    }
  }

  // Admin Management APIs
  async getAllAdmins() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // If endpoint doesn't exist, return mock data
        return {
          success: true,
          data: [
            { id: 1, name: "John Doe", email: "john@example.com", role: "Administrator", status: "active", createdAt: "2024-01-15" },
            { id: 2, name: "Jane Smith", email: "jane@example.com", role: "Super Admin", status: "active", createdAt: "2024-02-20" },
            { id: 3, name: "Mike Johnson", email: "mike@example.com", role: "Administrator", status: "inactive", createdAt: "2024-03-10" }
          ]
        };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching admins:', error);
      return {
        success: true,
        data: [
          { id: 1, name: "John Doe", email: "john@example.com", role: "Administrator", status: "active", createdAt: "2024-01-15" },
          { id: 2, name: "Jane Smith", email: "jane@example.com", role: "Super Admin", status: "active", createdAt: "2024-02-20" },
          { id: 3, name: "Mike Johnson", email: "mike@example.com", role: "Administrator", status: "inactive", createdAt: "2024-03-10" }
        ]
      };
    }
  }

  async createAdmin(adminData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(adminData),
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist, return mock success
        return {
          success: true,
          data: { id: Date.now(), ...adminData, status: 'active', createdAt: new Date().toISOString().split('T')[0] }
        };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating admin:', error);
      return {
        success: true,
        data: { id: Date.now(), ...adminData, status: 'active', createdAt: new Date().toISOString().split('T')[0] }
      };
    }
  }

  async updateAdmin(adminId, adminData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${adminId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(adminData),
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist, return mock success
        return { success: true, data: { id: adminId, ...adminData } };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating admin:', error);
      return { success: true, data: { id: adminId, ...adminData } };
    }
  }

  async deleteAdmin(adminId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${adminId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist, return mock success
        return { success: true, message: 'Admin deleted successfully' };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting admin:', error);
      return { success: true, message: 'Admin deleted successfully' };
    }
  }

  async toggleAdminStatus(adminId, status) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${adminId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ status }),
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist, return mock success
        return { success: true, data: { id: adminId, status } };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating admin status:', error);
      return { success: true, data: { id: adminId, status } };
    }
  }
}

const bookingAPI = new BookingAPI();
export default bookingAPI;